import { defineGlobal } from "@faire/globals";
import type { ProductArea } from "@faire/types-observability-api/faire/logging/ProductArea";

import { Attribution, getAttribution } from "./__internal__/attribution";
import { browserLoggingHandler } from "./__internal__/logging/browserLoggingHandler";
import {
  getError,
  isJestEnvironment,
  isNodeEnvironment,
  normalizeArgs,
} from "./__internal__/logging/utils";
import {
  Attributes,
  LoggingContext,
  LoggingHandler,
  LoggingInterceptor,
  NegativeLoggingContext,
  PositiveLoggingContext,
} from "./logging/types";
import { Tracer } from "./tracing";

export { getDatadogRum } from "./__internal__/logging/browserLoggingHandler";

type LoggerOptions<Sync extends boolean = false> = {
  attributes?: Attributes;
  prefix?: string;
  /**
   * When true, `info` / `debug` / `warn` / `error` / `fatal` return the same
   * promise as the underlying handler (so callers can `await` delivery before
   * continuing, e.g. before interactive prompts). Default is fire-and-forget.
   */
  syncDelivery?: Sync;
};

type LogMethodReturn<Sync extends boolean> = Sync extends true
  ? Promise<void>
  : void;

export class Logger<const Sync extends boolean = false> {
  private attribution: Attribution;

  private constructor(
    productArea: keyof typeof ProductArea,
    private readonly options?: LoggerOptions<Sync>
  ) {
    this.attribution = getAttribution(productArea);
    if (getHandler()) {
      return;
    }
    let handler: LoggingHandler | Promise<LoggingHandler>;

    if (isJestEnvironment()) {
      // Use require (not dynamic import) so path aliases resolve when Nx loads
      // executors without a global tsconfig-paths hook (Nx 23 native TS strip).
      handler = Promise.resolve(
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        require("./__internal__/logging/unitTestLoggingHandler")
          .unitTestLoggingHandler
      );
    } else if (typeof window !== "undefined") {
      handler = browserLoggingHandler;
    } else if (isNodeEnvironment()) {
      // Use require (not dynamic import): Nx executors load this via CJS without
      // a bundler, and ignored dynamic imports fail module resolution there.
      // Edge instrumentation must not statically import this module — keep
      // Node-only callers (e.g. retailer request interceptors) behind lazy loaders.
      handler = Promise.resolve(
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        require("./logging/nodeLoggingHandler").nodeLoggingHandler
      );
    } else if (
      typeof (globalThis as { importScripts?: unknown }).importScripts ===
        "function" ||
      // Instrumentation may transitively import Logger on Edge; never load Node I/O.
      process.env.NEXT_RUNTIME === "edge"
    ) {
      handler = () => undefined;
    } else {
      throw new Error("Logging environment is not supported.");
    }

    void setHandler(handler);
  }

  info(message: string, attributes?: Attributes): LogMethodReturn<Sync> {
    return this.positiveLog("info", message, attributes);
  }

  debug(message: string, attributes?: Attributes): LogMethodReturn<Sync> {
    return this.positiveLog(
      "debug",
      message,
      attributes
    ) as LogMethodReturn<Sync>;
  }

  warn(message: string, attributes?: Attributes): LogMethodReturn<Sync>;
  warn(
    message: string,
    error: unknown,
    attributes?: Attributes
  ): LogMethodReturn<Sync>;
  warn(error: unknown, attributes?: Attributes): LogMethodReturn<Sync>;
  warn(...args: [unknown, unknown?, Attributes?]): LogMethodReturn<Sync> {
    return this.negativeLog("warn", args) as LogMethodReturn<Sync>;
  }

  error(message: string, attributes?: Attributes): LogMethodReturn<Sync>;
  error(
    message: string,
    error: unknown,
    attributes?: Attributes
  ): LogMethodReturn<Sync>;
  error(error: unknown, attributes?: Attributes): LogMethodReturn<Sync>;
  error(...args: [unknown, unknown?, Attributes?]): LogMethodReturn<Sync> {
    return this.negativeLog("error", args) as LogMethodReturn<Sync>;
  }

  fatal(message: string, attributes?: Attributes): LogMethodReturn<Sync>;
  fatal(
    message: string,
    error: unknown,
    attributes?: Attributes
  ): LogMethodReturn<Sync>;
  fatal(error: unknown, attributes?: Attributes): LogMethodReturn<Sync>;
  fatal(...args: [unknown, unknown?, Attributes?]): LogMethodReturn<Sync> {
    return this.negativeLog("fatal", args) as LogMethodReturn<Sync>;
  }

  /**
   * Add an interceptor to modify the values of a given log.
   * The name identifies the interceptor in error messages and replaces any
   * existing interceptor registered with the same name.
   */
  static addInterceptor(interceptor: LoggingInterceptor, name: string) {
    const interceptors = [...(getInterceptors() ?? [])];
    const existingIndex = interceptors.findIndex(
      (entry) => entry.name === name
    );
    const entry = { interceptor, name };
    if (existingIndex === -1) {
      interceptors.push(entry);
    } else {
      interceptors[existingIndex] = entry;
    }
    setInterceptors(interceptors);
  }

  static getLogger<P extends keyof typeof ProductArea>(
    productArea: P,
    options?: Omit<LoggerOptions, "syncDelivery"> & { syncDelivery?: false }
  ): { logger: Logger<false> };
  static getLogger<P extends keyof typeof ProductArea>(
    productArea: P,
    options: Omit<LoggerOptions, "syncDelivery"> & { syncDelivery: true }
  ): { logger: Logger<true> };
  static getLogger<Sync extends boolean>(
    productArea: keyof typeof ProductArea,
    options?: LoggerOptions<Sync>
  ): { logger: Logger<Sync> } {
    return { logger: new Logger<Sync>(productArea, options) };
  }

  private negativeLog(
    level: NegativeLoggingContext["level"],
    args: [unknown, unknown?, Attributes?]
  ): void | Promise<void> {
    const { message, attributes, error: normalizedError } = normalizeArgs(args);
    const error = getError({
      message,
      attributes,
      error: normalizedError,
    });

    if (level !== "warn") {
      // Mark the current span as an error for error/fatal logs.
      Tracer.setError(error.instance);
    }

    return this.log({
      level,
      message,
      attributes,
      error,
      ...this.attribution,
    });
  }

  private positiveLog(
    level: PositiveLoggingContext["level"],
    message: string,
    attributes: Attributes | undefined
  ): LogMethodReturn<Sync> {
    return this.log({
      level,
      message,
      attributes,
      ...this.attribution,
    });
  }

  private log(context: LoggingContext): LogMethodReturn<Sync> {
    if (!this.options?.syncDelivery) {
      return this.logAsync(context) as LogMethodReturn<Sync>;
    }
    return this.logSync(context) as LogMethodReturn<Sync>;
  }

  private async logAsync(context: LoggingContext) {
    if (this.options?.attributes || context.attributes) {
      context.attributes = {
        ...this.options?.attributes,
        ...context.attributes,
      };
    }
    if (this.options?.prefix) {
      context.message = `${this.options.prefix} ${context.message}`;
    }

    const interceptors = getInterceptors() ?? [];

    for (const { interceptor, name } of interceptors) {
      try {
        context = await interceptor(context);
      } catch {
        context.message = `[Error in interceptor ${name}]: ${context.message}`;
      }
    }
    const handler = await getHandler();
    if (!handler) {
      throw new Error("Invariant: Logging handler is not set.");
    }
    await handler(context);
  }

  private async logSync(context: LoggingContext): Promise<void> {
    if (this.options?.attributes || context.attributes) {
      context.attributes = {
        ...this.options?.attributes,
        ...context.attributes,
      };
    }
    if (this.options?.prefix) {
      context.message = `${this.options.prefix} ${context.message}`;
    }

    const interceptors = getInterceptors() ?? [];

    for (const { interceptor, name } of interceptors) {
      try {
        const interceptedContext = interceptor(context);
        if (interceptedContext instanceof Promise) {
          throw new Error("Invariant: Async interceptor detected.");
        }
        context = interceptedContext;
      } catch {
        context.message = `[Error in interceptor ${name}]: ${context.message}`;
      }
    }
    const rawHandler = getHandler();
    if (!rawHandler) {
      throw new Error("Invariant: Logging handler is not set.");
    }
    const handler =
      rawHandler instanceof Promise ? await rawHandler : rawHandler;
    await handler(context);
  }
}

/**
 * Store the interceptors at the global scope, to ensure they are consistent across instances of the `Logger` class.
 * This is necessary because Next.js creates multiple copies of this module, resulting in multiple instances of the `Logger` class.
 */
export const { get: getInterceptors, set: setInterceptors } = defineGlobal<
  Array<{ interceptor: LoggingInterceptor; name: string }>
>("logging.interceptors");

export const { get: getHandler, set: setHandler } = defineGlobal<
  LoggingHandler | Promise<LoggingHandler>
>("logging.handler");
